import { useTheme } from "@mui/material";
import { isEmpty } from "lodash";
import { useEffect, useMemo } from "react";

import HierarchicalAutocomplete from "@/Components/v2/atoms/Autocomplete/HierarchicalAutocomplete";
import { HierarchicalOption } from "@/Components/v2/atoms/Autocomplete/types";
import { InputAdornment } from "@/Components/v2/atoms/InputAdornment";
import { CircularProgress } from "@/Components/v2/atoms/Progress";
import { Stack } from "@/Components/v2/atoms/Stack";
import { TextField } from "@/Components/v2/atoms/TextField";
import { Typography } from "@/Components/v2/atoms/Typography";
import { MultiSelectV2 } from "@/Components/v2/molecule/LabelledAutoComplete/v2/MultiSelectV2";
import { SingleSelectV2 } from "@/Components/v2/molecule/LabelledAutoComplete/v2/SingleSelectV2";
import { NodeHierarchyBreadcrumb } from "@/Components/v2/organism/NodeHierarchyBreadcrumb";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useConfig } from "@/hooks/useConfig";
import { useChannels } from "@/pages/channel-roi/api/getChannels";
import { NodeHierarchy } from "@/pages/scenario-builder/utils/nodes";
import { Layer } from "@/Utils/hierarchy";
import { getMappedGeo, getMappedName } from "@/Utils/mapping-utils";

export enum FilterType {
  BusinessUnit = "businessUnit",
  MultiSelect = "multiSelect",
  SingleSelect = "singleSelect",
  SingleSelectWithAll = "singleSelectWithAll",
}

export enum FilterId {
  BusinessUnit = "businessUnit",
  Geography = "geography",
  Channel = "channel",
  Funnel = "funnel",
  Campaign = "campaign",
  Format = "format",
  Publisher = "publisher",
  Custom = "custom",
}

interface Option {
  label: string;
  value: string;
  selected?: boolean;
}

export interface Filter {
  id: FilterId | string;
  label: string;
  type: FilterType;
  value: string[] | string | null;
  options?: Option[] | HierarchicalOption[];
  onChange: (value: any) => void;
}

interface FilterComponentProps {
  filter: Filter;
  dropdownWidth: number;
  businessUnitOptions: HierarchicalOption[];
  geoOptions: Option[];
  channelOptions: Option[];
  funnelOptions: Option[];
}

interface HeaderProps {
  availableFilters: Filter[];
}

// Constants
const DROPDOWN_WIDTH = 280;

const FilterComponent: React.FC<FilterComponentProps> = ({
  filter,
  dropdownWidth,
  businessUnitOptions,
  geoOptions,
  channelOptions,
  funnelOptions,
}) => {
  const options = getOptionsForFilter(
    filter,
    businessUnitOptions,
    geoOptions,
    channelOptions,
    funnelOptions
  );

  switch (filter.type) {
    case FilterType.BusinessUnit:
      return (
        <HierarchicalAutocomplete
          sx={{
            width: dropdownWidth,
            "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
              paddingLeft: "12px",
              borderRadius: "4px",
            },
          }}
          id={filter.id}
          disableClearable
          options={options as HierarchicalOption[]}
          defaultValue={(options as HierarchicalOption[])[0]}
          value={filter.value}
          onChange={(value) => filter.onChange(value?.value)}
          renderInput={(params) => (
            <TextField
              {...(params as any)}
              inputProps={{
                ...params.inputProps,
                value: "",
              }}
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography fontSize="16px" fontWeight="600" color="text.secondary">
                      {filter.label}
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      );
    case FilterType.MultiSelect:
      return (
        <MultiSelectV2
          width={300}
          multiple
          disableClearable
          id={filter.id}
          label={filter.label}
          options={options as Option[]}
          value={filter.value as string[]}
          onChange={filter.onChange}
        />
      );
    case FilterType.SingleSelect:
      return (
        <SingleSelectV2
          width={dropdownWidth}
          id={filter.id}
          label={filter.label}
          options={options as Option[]}
          value={filter.value as string}
          onChange={filter.onChange}
          multiple={false}
        />
      );
    case FilterType.SingleSelectWithAll: {
      const baseOptions = options as Option[];
      const optionsWithAll = [{ label: "All", value: "all", selected: false }, ...baseOptions];

      return (
        <SingleSelectV2
          width={240}
          id={filter.id}
          label={filter.label}
          options={optionsWithAll}
          value={filter.value as string}
          onChange={(value) => filter.onChange(value)}
          multiple={false}
          disableClearable
        />
      );
    }
    default:
      return null;
  }
};

export const Header: React.FC<HeaderProps> = ({ availableFilters }) => {
  const theme = useTheme();
  const { config } = useConfig();

  const targetNode = availableFilters.find((filter) => filter.id === FilterId.BusinessUnit)
    ?.value as string | null;

  const { initInfo: info, nodes, loading: areNodesLoading } = useInfo();
  const { data: allChannels } = useChannels({ type: "all" });

  // const noBrandLayer = !nodes?.getAvailableLayers().includes(Layer.Brand);
  const allFunnelStages = Array.from(new Set(nodes?.getAvailableLayers()));

  const businessUnitHierarchyOptions: HierarchicalOption[] = useMemo(() => {
    if (!nodes) return [];

    const rootNodeId = 0;
    const mapNodeToOption = (node: NodeHierarchy): HierarchicalOption => ({
      id: node.id,
      label: node.label,
      value: node.uuid,
      children: node.children.map(mapNodeToOption),
      alwaysOpen: node.id === rootNodeId,
    });

    const hierarchicalOptions = nodes.getHierarchy();
    return hierarchicalOptions.map(mapNodeToOption);
  }, [nodes]);

  const filteredGeoOptions = useMemo(() => {
    if (!info?.geographies || !targetNode || !nodes) return [];

    return generateGeoOptions([...info.geographies]);
  }, [info?.geographies, targetNode, nodes]);

  const channelOptions = useMemo(() => {
    if (!allChannels) return [];

    return allChannels
      ?.map((channel) => ({
        label: getMappedName(channel) || channel,
        value: channel,
        selected: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allChannels]);

  const layerOptions = useMemo(() => {
    const hasBrandLayer = allFunnelStages.includes(Layer.Brand);
    const hasCustomerLayer = allFunnelStages.includes(Layer.Portfolio);

    const filteredFunnelStages = allFunnelStages.filter((funnelStage) => {
      if (funnelStage == Layer.Portfolio) return false;
      return true;
    });

    if (!hasBrandLayer && hasCustomerLayer) {
      filteredFunnelStages.push(Layer.Brand);
    }

    return filteredFunnelStages.map((funnelStage) => ({
      label: config.getLayerLabel(funnelStage as Layer),
      value: funnelStage,
      selected: false,
    }));
  }, [allFunnelStages]);

  useEffect(() => {
    if (!availableFilters || !targetNode) return;
    availableFilters.forEach((filter) => {
      if (!isEmpty(filter.value) || filter.id === FilterId.BusinessUnit) return;

      const options = getOptionsForFilter(
        filter,
        businessUnitHierarchyOptions,
        filteredGeoOptions,
        channelOptions,
        layerOptions
      );

      let value;
      if (filter.type === FilterType.SingleSelect && options.length > 0) {
        value = (options[0] as Option)?.value;
      } else if (filter.type === FilterType.SingleSelectWithAll) {
        value = "all";
      } else {
        value = options.map((option) => option.value);
      }

      filter.onChange(value);
    });
  }, [targetNode, filteredGeoOptions, channelOptions]);

  if (areNodesLoading || !nodes) {
    return <CircularProgress size={150} sx={{ marginY: "30rem" }} />;
  }

  return (
    <Stack
      direction="column"
      spacing={2}
      margin={4}
      marginY={2}
      padding={3}
      border={`1px solid ${theme.palette.section.border}`}
      borderRadius={4}
    >
      <Stack direction="row" justifyContent="start" spacing={2}>
        {availableFilters.map((filter) => (
          <FilterComponent
            key={filter.id}
            filter={filter}
            dropdownWidth={DROPDOWN_WIDTH}
            businessUnitOptions={businessUnitHierarchyOptions}
            geoOptions={filteredGeoOptions}
            channelOptions={channelOptions}
            funnelOptions={layerOptions}
          />
        ))}
      </Stack>
      {targetNode && <NodeHierarchyBreadcrumb nodeUUID={targetNode} />}
    </Stack>
  );
};

export default Header;

// Helper functions
const generateGeoOptions = (geos: string[]): Option[] => {
  return geos
    .map((geo) => ({
      label: getMappedGeo(geo) || getMappedName(geo) || geo,
      value: geo,
      selected: false,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const getOptionsForFilter = (
  filter: Filter,
  businessUnitOptions: HierarchicalOption[],
  geoOptions: Option[],
  channelOptions: Option[],
  funnelOptions: Option[]
): Option[] | HierarchicalOption[] => {
  if (filter.options?.length) {
    return filter.options;
  }

  switch (filter.id) {
    case FilterId.BusinessUnit:
      return businessUnitOptions;
    case FilterId.Geography:
      return geoOptions;
    case FilterId.Channel:
      return channelOptions;
    case FilterId.Funnel:
      return funnelOptions;
    default:
      return [];
  }
};
